import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, Inbox, CheckCircle2, RefreshCw, Plus, Pencil, Trash2, CalendarDays, Trophy, Star } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, setToken } from "@/lib/api";
import { displayTime } from "@/lib/calendar";
import ThemeToggle from "@/components/site/ThemeToggle";

const STATUS = ["new", "contacted", "booked", "closed"];
const RESULT_STATUS = ["pending", "approved", "featured"];
const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const LOCATIONS = ["Synergy Fitness", "Afterburn Performance"];
const programLabel = {
  "in-person": "In-Person",
  hybrid: "Hybrid",
  online: "Online",
  bootcamp: "Bootcamp",
};

const emptyClass = {
  day: "MON",
  name: "",
  location: "Synergy Fitness",
  address: "",
  description: "",
  start: "19:00",
  duration_min: 60,
  urgency: "",
  order: 0,
  active: true,
};

const URGENCY_PRESETS = ["Filling up", "3 spots left", "2 spots left", "1 spot left", "Almost full", "Waitlist only"];

const emptyReview = {
  reviewer: "Verified Member",
  rating: 5,
  class_name: "",
  review: "",
  date: "",
  featured: true,
  order: 0,
};

export default function Admin() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [classes, setClasses] = useState([]);
  const [results, setResults] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyClass);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [reviewForm, setReviewForm] = useState(emptyReview);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const load = async () => {
    try {
      await api.get("/auth/me");
      const [leadsRes, classesRes, resultsRes, reviewsRes] = await Promise.all([
        api.get("/leads"),
        api.get("/classes/all"),
        api.get("/results"),
        api.get("/classpass/reviews/all"),
      ]);
      setLeads(leadsRes.data);
      setClasses(classesRes.data);
      setResults(resultsRes.data);
      setReviews(reviewsRes.data);
    } catch (e) {
      setToken(null);
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}?status=${status}`);
      toast("Status updated");
    } catch {
      toast("Could not update status");
    }
  };

  const logout = () => {
    setToken(null);
    navigate("/login");
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyClass, order: classes.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      day: c.day, name: c.name, location: c.location, address: c.address || "",
      description: c.description || "", start: c.start, duration_min: c.duration_min || 60,
      urgency: c.urgency || "",
      order: c.order || 0, active: c.active !== false,
    });
    setDialogOpen(true);
  };

  const saveClass = async () => {
    if (!form.name.trim()) return toast("Class name is required");
    try {
      if (editingId) {
        const { data } = await api.put(`/classes/${editingId}`, form);
        setClasses((cs) => cs.map((c) => (c.id === editingId ? data : c)).sort((a, b) => a.order - b.order));
        toast("Class updated");
      } else {
        const { data } = await api.post("/classes", form);
        setClasses((cs) => [...cs, data].sort((a, b) => a.order - b.order));
        toast("Class added");
      }
      setDialogOpen(false);
    } catch {
      toast("Could not save class");
    }
  };

  const deleteClass = async (id) => {
    setClasses((cs) => cs.filter((c) => c.id !== id));
    try {
      await api.delete(`/classes/${id}`);
      toast("Class deleted");
    } catch {
      toast("Could not delete class");
    }
  };

  const setResultStatus = async (id, status) => {
    setResults((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await api.patch(`/results/${id}?status=${status}`);
      toast(status === "featured" ? "Result featured on site" : `Marked ${status}`);
    } catch {
      toast("Could not update result");
    }
  };

  const deleteResult = async (id) => {
    setResults((rs) => rs.filter((r) => r.id !== id));
    try {
      await api.delete(`/results/${id}`);
      toast("Result deleted");
    } catch {
      toast("Could not delete result");
    }
  };

  // ---- ClassPass review handlers ----
  const openNewReview = () => {
    setEditingReviewId(null);
    setReviewForm({ ...emptyReview, order: reviews.length + 1 });
    setReviewDialogOpen(true);
  };
  const openEditReview = (r) => {
    setEditingReviewId(r.id);
    setReviewForm({
      reviewer: r.reviewer || "Verified Member",
      rating: r.rating || 5,
      class_name: r.class_name || "",
      review: r.review || "",
      date: r.date || "",
      featured: r.featured !== false,
      order: r.order || 0,
    });
    setReviewDialogOpen(true);
  };
  const saveReview = async () => {
    if (!reviewForm.review.trim()) return toast("Review text is required");
    try {
      if (editingReviewId) {
        const { data } = await api.put(`/classpass/reviews/${editingReviewId}`, reviewForm);
        setReviews((rs) => rs.map((r) => (r.id === editingReviewId ? data : r)));
        toast("Review updated");
      } else {
        const { data } = await api.post("/classpass/reviews", reviewForm);
        setReviews((rs) => [...rs, data]);
        toast("Review added");
      }
      setReviewDialogOpen(false);
    } catch {
      toast("Could not save review");
    }
  };
  const deleteReview = async (id) => {
    setReviews((rs) => rs.filter((r) => r.id !== id));
    try {
      await api.delete(`/classpass/reviews/${id}`);
      toast("Review deleted");
    } catch {
      toast("Could not delete review");
    }
  };
  const toggleFeatured = async (r) => {
    const next = { ...r, featured: !r.featured };
    setReviews((rs) => rs.map((x) => (x.id === r.id ? next : x)));
    try {
      await api.put(`/classpass/reviews/${r.id}`, {
        reviewer: r.reviewer, rating: r.rating, class_name: r.class_name,
        review: r.review, date: r.date, order: r.order, featured: !r.featured,
      });
    } catch {
      toast("Could not update");
    }
  };
  const bulkImport = async () => {
    // Parse plain-text reviews. Blocks separated by blank lines.
    // Optional first line "Class Name — Date" then review body on next lines.
    const blocks = bulkText.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
    if (blocks.length === 0) return toast("Paste at least one review");
    let added = 0;
    for (const [i, block] of blocks.entries()) {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      let class_name = "", date = "", reviewText = block;
      // Try to parse first line "Class — 2025-05-01" or "Class · 5/1/2025"
      const headerMatch = lines[0].match(/^(.+?)\s+[—·-]\s+(\d{1,4}[-/]\d{1,2}[-/]\d{1,4})\s*$/);
      if (headerMatch && lines.length > 1) {
        class_name = headerMatch[1].trim();
        const rawDate = headerMatch[2].trim();
        // Convert to ISO if possible
        const parts = rawDate.split(/[-/]/);
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            date = `${parts[0]}-${parts[1].padStart(2, "0")}-${parts[2].padStart(2, "0")}`;
          } else {
            // M/D/YYYY
            date = `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
          }
        }
        reviewText = lines.slice(1).join(" ");
      }
      try {
        await api.post("/classpass/reviews", {
          reviewer: "Verified Member",
          rating: 5,
          class_name,
          review: reviewText,
          date,
          featured: true,
          order: reviews.length + i + 1,
        });
        added++;
      } catch { /* skip failed rows */ }
    }
    toast(`Imported ${added} of ${blocks.length} review(s)`);
    setBulkText("");
    setBulkOpen(false);
    load();
  };

  const stats = [
    { label: "Total leads", value: leads.length, icon: Users },
    { label: "New", value: leads.filter((l) => l.status === "new").length, icon: Inbox },
    { label: "Booked", value: leads.filter((l) => l.status === "booked").length, icon: CheckCircle2 },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <header className="border-b border-[#222] px-5 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-black/70 backdrop-blur-xl z-10">
        <div>
          <p className="font-display text-white text-xl leading-none tracking-tight">
            GETFIT<span className="text-[#005EEA]">/</span>DASHBOARD
          </p>
          <p className="font-mono-accent text-[10px] text-[#666] mt-1">Leads & consultations</p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/admin/clinics"
            className="font-mono-accent text-xs bg-[#005EEA] text-white px-4 py-2 hover:bg-[#0047b3] transition-colors inline-flex items-center gap-2"
            data-testid="admin-clinics-link"
          >
            Clinics →
          </Link>
          <ThemeToggle />
          <button
            onClick={() => { setLoading(true); load(); }}
            className="font-mono-accent text-xs border border-[#222] text-[#A0A0A0] px-4 py-3 hover:text-white hover:border-white/30 transition-colors flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            onClick={logout}
            className="font-mono-accent text-xs bg-[#111] border border-[#222] text-white px-4 py-3 hover:bg-[#005EEA] transition-colors flex items-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      <main className="px-5 md:px-10 py-10 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-3 gap-4 mb-10">
          {stats.map((s) => (
            <div key={s.label} className="border border-[#222] bg-[#0b0b0b] p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <s.icon className="text-[#005EEA] mb-4" size={22} />
              <p className="font-display text-white text-4xl md:text-6xl leading-none">{s.value}</p>
              <p className="font-mono-accent text-[10px] text-[#666] mt-2">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="border border-[#222] bg-[#0b0b0b]">
          <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between">
            <h2 className="font-display text-white text-xl">Consultation Requests</h2>
          </div>

          {loading ? (
            <p className="p-10 text-center text-[#666] font-mono-accent text-xs">Loading…</p>
          ) : leads.length === 0 ? (
            <p className="p-10 text-center text-[#666] font-mono-accent text-xs" data-testid="empty-leads">
              No leads yet. Share your site to start collecting.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-[#222] font-mono-accent text-[10px] text-[#666]">
                    <th className="px-6 py-4 font-normal">Name</th>
                    <th className="px-6 py-4 font-normal">Contact</th>
                    <th className="px-6 py-4 font-normal">Program</th>
                    <th className="px-6 py-4 font-normal">Goal</th>
                    <th className="px-6 py-4 font-normal">Date</th>
                    <th className="px-6 py-4 font-normal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-[#161616] hover:bg-[#111] transition-colors align-top" data-testid={`lead-row-${l.id}`}>
                      <td className="px-6 py-5 text-white font-medium">{l.name}</td>
                      <td className="px-6 py-5 text-[#A0A0A0] text-sm">
                        <div>{l.email}</div>
                        <div className="text-[#666]">{l.phone}</div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="font-mono-accent text-[10px] text-[#005EEA] border border-[#005EEA]/40 px-2 py-1">
                          {programLabel[l.program] || l.program}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-[#A0A0A0] text-sm max-w-xs">{l.goal}</td>
                      <td className="px-6 py-5 text-[#666] text-sm whitespace-nowrap">
                        {new Date(l.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-5">
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger className="w-[130px] bg-[#111] border border-[#222] text-white rounded-none text-xs" data-testid={`status-select-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border border-[#222] text-white rounded-none">
                            {STATUS.map((s) => (
                              <SelectItem key={s} value={s} className="rounded-none focus:bg-[#005EEA] focus:text-white capitalize">
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Class schedule management */}
        <div className="border border-[#222] bg-[#0b0b0b] mt-10" data-testid="admin-schedule">
          <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between">
            <h2 className="font-display text-white text-xl flex items-center gap-3">
              <CalendarDays size={18} className="text-[#005EEA]" /> Class Schedule
            </h2>
            <button
              onClick={openNew}
              className="font-mono-accent text-xs bg-[#005EEA] text-white px-4 py-3 hover:bg-[#0047b3] transition-colors flex items-center gap-2"
              data-testid="add-class-btn"
            >
              <Plus size={13} /> Add Class
            </button>
          </div>
          {classes.length === 0 ? (
            <p className="p-10 text-center text-[#666] font-mono-accent text-xs">No classes yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="classes-table">
                <thead>
                  <tr className="border-b border-[#222] font-mono-accent text-[10px] text-[#666]">
                    <th className="px-6 py-4 font-normal">Day</th>
                    <th className="px-6 py-4 font-normal">Class</th>
                    <th className="px-6 py-4 font-normal">Location</th>
                    <th className="px-6 py-4 font-normal">Time</th>
                    <th className="px-6 py-4 font-normal text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classes.map((c) => (
                    <tr key={c.id} className="border-b border-[#161616] hover:bg-[#111] transition-colors" data-testid={`class-admin-row-${c.id}`}>
                      <td className="px-6 py-4 text-[#005EEA] font-mono-accent text-xs">{c.day}</td>
                      <td className="px-6 py-4 text-white font-medium">
                        {c.name}
                        {c.active === false && <span className="ml-2 text-[10px] text-[#666] font-mono-accent">(hidden)</span>}
                      </td>
                      <td className="px-6 py-4 text-[#A0A0A0] text-sm">{c.location}</td>
                      <td className="px-6 py-4 text-[#A0A0A0] text-sm whitespace-nowrap">{displayTime(c.start)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEdit(c)} className="p-2 text-[#A0A0A0] hover:text-white hover:bg-[#005EEA] transition-colors" data-testid={`edit-class-${c.id}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => deleteClass(c.id)} className="p-2 text-[#A0A0A0] hover:text-white hover:bg-red-600 transition-colors" data-testid={`delete-class-${c.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Client results / success stories */}
        <div className="border border-[#222] bg-[#0b0b0b] mt-10" data-testid="admin-results">
          <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between">
            <h2 className="font-display text-white text-xl flex items-center gap-3">
              <Trophy size={18} className="text-[#005EEA]" /> Client Results
            </h2>
            <span className="font-mono-accent text-[10px] text-[#666]">
              {results.filter((r) => r.status === "featured").length} featured on site
            </span>
          </div>
          {results.length === 0 ? (
            <p className="p-10 text-center text-[#666] font-mono-accent text-xs" data-testid="empty-results">
              No client results submitted yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="results-table">
                <thead>
                  <tr className="border-b border-[#222] font-mono-accent text-[10px] text-[#666]">
                    <th className="px-6 py-4 font-normal">Client</th>
                    <th className="px-6 py-4 font-normal">Result</th>
                    <th className="px-6 py-4 font-normal">Story</th>
                    <th className="px-6 py-4 font-normal">Consent</th>
                    <th className="px-6 py-4 font-normal">Status</th>
                    <th className="px-6 py-4 font-normal text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.id} className="border-b border-[#161616] hover:bg-[#111] transition-colors align-top" data-testid={`result-row-${r.id}`}>
                      <td className="px-6 py-5">
                        <div className="text-white font-medium">{r.name}</div>
                        <div className="text-[#666] text-sm">{r.email}</div>
                      </td>
                      <td className="px-6 py-5 text-[#A0A0A0] text-sm whitespace-nowrap">
                        <div className="text-[#005EEA] font-mono-accent text-xs">{r.metric || "—"}</div>
                        <div className="text-[#666]">{r.program} {r.timeframe && `· ${r.timeframe}`}</div>
                      </td>
                      <td className="px-6 py-5 text-[#A0A0A0] text-sm max-w-xs">{r.story}</td>
                      <td className="px-6 py-5">
                        <span className={`font-mono-accent text-[10px] px-2 py-1 border ${r.consent ? "text-[#005EEA] border-[#005EEA]/40" : "text-[#666] border-[#222]"}`}>
                          {r.consent ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <Select value={r.status} onValueChange={(v) => setResultStatus(r.id, v)}>
                          <SelectTrigger className="w-[130px] bg-[#111] border border-[#222] text-white rounded-none text-xs capitalize" data-testid={`result-status-${r.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111] border border-[#222] text-white rounded-none">
                            {RESULT_STATUS.map((s) => (
                              <SelectItem key={s} value={s} className="rounded-none focus:bg-[#005EEA] focus:text-white capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-end">
                          <button onClick={() => deleteResult(r.id)} className="p-2 text-[#A0A0A0] hover:text-white hover:bg-red-600 transition-colors" data-testid={`delete-result-${r.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ClassPass reviews management */}
        <div className="border border-[#222] bg-[#0b0b0b] mt-10" data-testid="admin-classpass">
          <div className="px-6 py-4 border-b border-[#222] flex items-center justify-between flex-wrap gap-3">
            <h2 className="font-display text-white text-xl flex items-center gap-3">
              <Star size={18} className="text-[#005EEA]" /> ClassPass Reviews
            </h2>
            <div className="flex items-center gap-2">
              <span className="font-mono-accent text-[10px] text-[#666]">
                {reviews.filter((r) => r.featured).length} featured · {reviews.length} total
              </span>
              <button
                onClick={() => setBulkOpen(true)}
                className="font-mono-accent text-xs border border-[#222] text-[#A0A0A0] px-4 py-3 hover:text-white hover:border-white/30 transition-colors"
                data-testid="bulk-import-btn"
              >
                Bulk Import
              </button>
              <button
                onClick={openNewReview}
                className="font-mono-accent text-xs bg-[#005EEA] text-white px-4 py-3 hover:bg-[#0047b3] transition-colors flex items-center gap-2"
                data-testid="add-review-btn"
              >
                <Plus size={13} /> Add Review
              </button>
            </div>
          </div>
          {reviews.length === 0 ? (
            <p className="p-10 text-center text-[#666] font-mono-accent text-xs" data-testid="empty-reviews">
              No reviews yet. Add or bulk-import from ClassPass.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="reviews-table">
                <thead>
                  <tr className="border-b border-[#222] font-mono-accent text-[10px] text-[#666]">
                    <th className="px-6 py-4 font-normal">Class</th>
                    <th className="px-6 py-4 font-normal">Review</th>
                    <th className="px-6 py-4 font-normal">Date</th>
                    <th className="px-6 py-4 font-normal">Featured</th>
                    <th className="px-6 py-4 font-normal text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((r) => (
                    <tr key={r.id} className="border-b border-[#161616] hover:bg-[#111] transition-colors align-top" data-testid={`review-row-${r.id}`}>
                      <td className="px-6 py-4 text-[#005EEA] font-mono-accent text-xs whitespace-nowrap">{r.class_name || "—"}</td>
                      <td className="px-6 py-4 text-[#A0A0A0] text-sm max-w-md">{r.review}</td>
                      <td className="px-6 py-4 text-[#666] text-sm whitespace-nowrap">{r.date || "—"}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleFeatured(r)}
                          data-testid={`toggle-featured-${r.id}`}
                          className={`font-mono-accent text-[10px] px-2 py-1 border transition-colors ${
                            r.featured
                              ? "text-[#005EEA] border-[#005EEA]/40 bg-[#005EEA]/10"
                              : "text-[#666] border-[#222] hover:text-white"
                          }`}
                        >
                          {r.featured ? "ON SITE" : "HIDDEN"}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => openEditReview(r)} className="p-2 text-[#A0A0A0] hover:text-white hover:bg-[#005EEA] transition-colors" data-testid={`edit-review-${r.id}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => deleteReview(r.id)} className="p-2 text-[#A0A0A0] hover:text-white hover:bg-red-600 transition-colors" data-testid={`delete-review-${r.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0b0b0b] border border-[#222] text-white rounded-none max-w-lg" data-testid="class-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit Class" : "Add Class"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Day</label>
                <Select value={form.day} onValueChange={(v) => setForm((f) => ({ ...f, day: v }))}>
                  <SelectTrigger className="bg-[#111] border-[#222] rounded-none" data-testid="class-day"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#222] text-white rounded-none">
                    {DAYS.map((d) => <SelectItem key={d} value={d} className="rounded-none focus:bg-[#005EEA] focus:text-white">{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Start time</label>
                <input type="time" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-start" />
              </div>
            </div>
            <div>
              <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Class name</label>
              <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-name" placeholder="e.g. L.I.F.T. Upper Body" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Location</label>
                <Select value={form.location} onValueChange={(v) => setForm((f) => ({ ...f, location: v }))}>
                  <SelectTrigger className="bg-[#111] border-[#222] rounded-none" data-testid="class-location"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#222] text-white rounded-none">
                    {LOCATIONS.map((l) => <SelectItem key={l} value={l} className="rounded-none focus:bg-[#005EEA] focus:text-white">{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Duration (min)</label>
                <input type="number" value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: parseInt(e.target.value) || 60 }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-duration" />
              </div>
            </div>
            <div>
              <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Address (optional — used for calendar)</label>
              <input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-address" placeholder="123 Main St, Toronto" />
            </div>
            <div>
              <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Description</label>
              <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-description" placeholder="Short description" />
            </div>
            <div>
              <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Urgency badge (optional — creates a scarcity tag on the class card)</label>
              <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                <input value={form.urgency} onChange={(e) => setForm((f) => ({ ...f, urgency: e.target.value }))}
                  className="bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-urgency" placeholder="e.g. 3 spots left" />
                <Select value="" onValueChange={(v) => setForm((f) => ({ ...f, urgency: v }))}>
                  <SelectTrigger className="bg-[#111] border-[#222] rounded-none text-xs text-[#A0A0A0] w-[150px]" data-testid="class-urgency-preset">
                    <SelectValue placeholder="Preset..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#222] text-white rounded-none">
                    {URGENCY_PRESETS.map((p) => (
                      <SelectItem key={p} value={p} className="rounded-none focus:bg-[#005EEA] focus:text-white">
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, urgency: "" }))}
                  className="font-mono-accent text-[10px] px-3 border border-[#222] text-[#A0A0A0] hover:text-white hover:border-white/30 transition-colors"
                  data-testid="class-urgency-clear"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Order</label>
                <input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="class-order" />
              </div>
              <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  className="accent-[#005EEA] w-4 h-4" data-testid="class-active" />
                <span className="font-mono-accent text-[10px] text-[#A0A0A0]">Visible on site</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="font-mono-accent text-xs border border-[#222] text-[#A0A0A0] px-5 py-3 hover:text-white transition-colors">
              Cancel
            </button>
            <button onClick={saveClass} className="font-mono-accent text-xs bg-[#005EEA] text-white px-5 py-3 hover:bg-[#0047b3] transition-colors" data-testid="save-class-btn">
              {editingId ? "Save Changes" : "Add Class"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ClassPass review dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-[#0b0b0b] border border-[#222] text-white rounded-none max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingReviewId ? "Edit Review" : "Add ClassPass Review"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Class name</label>
                <input value={reviewForm.class_name} onChange={(e) => setReviewForm((f) => ({ ...f, class_name: e.target.value }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="review-class" placeholder="e.g. Unbig Your Back" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Date (YYYY-MM-DD)</label>
                <input type="date" value={reviewForm.date} onChange={(e) => setReviewForm((f) => ({ ...f, date: e.target.value }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="review-date" />
              </div>
            </div>
            <div>
              <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Review text</label>
              <Textarea value={reviewForm.review} onChange={(e) => setReviewForm((f) => ({ ...f, review: e.target.value }))}
                rows={5}
                className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="review-text" placeholder="Paste the review here..." />
            </div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Rating</label>
                <input type="number" min="1" max="5" value={reviewForm.rating} onChange={(e) => setReviewForm((f) => ({ ...f, rating: parseInt(e.target.value) || 5 }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="review-rating" />
              </div>
              <div>
                <label className="font-mono-accent text-[10px] text-[#666] block mb-2">Order</label>
                <input type="number" value={reviewForm.order} onChange={(e) => setReviewForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                  className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA]" data-testid="review-order" />
              </div>
              <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
                <input type="checkbox" checked={reviewForm.featured} onChange={(e) => setReviewForm((f) => ({ ...f, featured: e.target.checked }))}
                  className="accent-[#005EEA] w-4 h-4" data-testid="review-featured" />
                <span className="font-mono-accent text-[10px] text-[#A0A0A0]">Show on site</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setReviewDialogOpen(false)} className="font-mono-accent text-xs border border-[#222] text-[#A0A0A0] px-5 py-3 hover:text-white transition-colors">
              Cancel
            </button>
            <button onClick={saveReview} className="font-mono-accent text-xs bg-[#005EEA] text-white px-5 py-3 hover:bg-[#0047b3] transition-colors" data-testid="save-review-btn">
              {editingReviewId ? "Save Changes" : "Add Review"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk import dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-[#0b0b0b] border border-[#222] text-white rounded-none max-w-2xl" data-testid="bulk-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Bulk Import Reviews</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-[#A0A0A0] text-sm">
              Paste one review per block, blocks separated by a blank line. Optional first line
              can be <code className="text-[#005EEA]">Class Name — YYYY-MM-DD</code> or
              <code className="text-[#005EEA]"> Class · M/D/YYYY</code>, followed by the review text.
            </p>
            <div className="border border-[#222] bg-black/50 p-3 font-mono text-xs text-[#666] whitespace-pre-wrap">
{`Unbig Your Back — 2025-05-10
My favourite group class!

Boxercise · 10/19/2025
This is a great class! Amazing workout while learning basic boxing moves...`}
            </div>
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={12}
              className="w-full bg-[#111] border border-[#222] text-white px-3 py-2.5 rounded-none focus:outline-none focus:ring-2 focus:ring-[#005EEA] font-mono text-xs"
              placeholder="Paste multiple reviews here..."
              data-testid="bulk-textarea"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setBulkOpen(false)} className="font-mono-accent text-xs border border-[#222] text-[#A0A0A0] px-5 py-3 hover:text-white transition-colors">
              Cancel
            </button>
            <button onClick={bulkImport} className="font-mono-accent text-xs bg-[#005EEA] text-white px-5 py-3 hover:bg-[#0047b3] transition-colors" data-testid="bulk-import-submit">
              Import Reviews
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
